import React from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import { useAppSelector } from '../../data/store'
import RequiredDocumentComponent from './RequiredDocumentComponent'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { RequiredDocument } from '../../data/types/RequiredDocument'
import { Enum } from '../../data/types/constants'
import { reportErrorToServer } from '../../error-logger'

function RequiredDocuments(): JSX.Element {
  const { id } = useParams()
  const dispatchEffect = useSideEffect()
  const { t } = useTranslation()

  const filteredCreditApp = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)

  const [currentUploads, setCurrentUploads] = React.useState<string[]>([])

  const applicantRequiredDocs =
    filteredCreditApp?.requiredDocuments.filter(
      (d) => d.applicantType !== 'coApplicant' && d.typeId !== Enum.EDocumentType.SignedCVT,
    ) ?? []

  const coApplicantRequiredDocs =
    filteredCreditApp?.requiredDocuments.filter((d) => d.applicantType === 'coApplicant') ?? []

  const uploadDocument = (files: File[], requiredDocument: RequiredDocument) => {
    setCurrentUploads((prev) => [...prev, requiredDocument.id])
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('files', file, file.name)
    })
    formData.set('payload', JSON.stringify(requiredDocument))
    dispatchEffect(creditApplicationEffects.postDocument(formData, id as string))
      .then(() => setCurrentUploads((prev) => prev.filter((i) => i !== requiredDocument.id)))
      .catch(reportErrorToServer)
  }

  const showBankAccountMismatchWarning = () => {
    return (
      ((filteredCreditApp?.requiredDocuments.some((d) => Object.values(Enum.EIncomeProofTypes).includes(d.typeId)) &&
        !filteredCreditApp?.allIncomesConfirmed) ||
        !filteredCreditApp?.completedVerifyAccountName) &&
      filteredCreditApp?.requiredExternalSteps.some((s) => s.status === Enum.ERequiredExternalStepStatus.Completed)
    )
  }

  return (
    <div className="paragraph" id="paragraph-document">
      {showBankAccountMismatchWarning() && (
        <div>
          <h3 style={{ fontWeight: 'bold', marginBottom: '2rem' }}>{t('uploadRequiredFile.requiredDocuments')}</h3>
          <span id="paragraph-table" style={{ fontWeight: 400 }}>
            {t('uploadRequiredFile.incomeDocumentsReason')}
          </span>
        </div>
      )}
      <div style={{ margin: '3rem 0', width: '100%' }} />
      {applicantRequiredDocs.length > 0 && (
        <RequiredDocumentComponent
          applicant={`${filteredCreditApp?.applicantFirstName} ${filteredCreditApp?.applicantLastName}`}
          requiredDocument={applicantRequiredDocs}
          uploadDocument={uploadDocument}
          currentUploads={currentUploads}
        />
      )}
      {coApplicantRequiredDocs.length > 0 && (
        <RequiredDocumentComponent
          applicant={`${filteredCreditApp?.coApplicantFirstName} ${filteredCreditApp?.coApplicantLastName}`}
          requiredDocument={coApplicantRequiredDocs}
          uploadDocument={uploadDocument}
          currentUploads={currentUploads}
        />
      )}
    </div>
  )
}

export default RequiredDocuments
